import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import userApi, { userCurrentPartyApi } from "../../api/userCurrent";
import { LOCAL_STORAGE_KEY } from "../../constants/localStorageKeys";
import { useDispatch } from "../../redux/hooks";
import { setCurrentUser, setLoggedIn, setCurrentParty } from "../../redux/slices/auth/authSlice";
import { GenericError } from "../../utils/errorHandling";

import useAuth from "./useAuth";
import useAuthErrorHandler from "./useAuthErrorHandler";
import useLocalStorage from "./useLocalStorage";

const REDIRECT_WHITELIST = ["/login", "/login/"];

const useAuthRehydrate = (): boolean => {
    const history = useHistory();
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const { csrfToken, currentUser } = useAuth();
    const [isHydrated, setIsHydrated] = useState(false);
    const [getInitialPath, setInitialPath, deleteInitialPath] = useLocalStorage(
        LOCAL_STORAGE_KEY.INITIAL_PATH,
    );
    const onAuthError = useAuthErrorHandler();

    useEffect(() => {
        let isCancelled = false;

        const fetch = async () => {
            setIsHydrated(false);

            if (!csrfToken) {
                if (!REDIRECT_WHITELIST.includes(pathname)) {
                    setInitialPath(pathname);
                }
                setIsHydrated(true);
                return;
            }

            if (currentUser) {
                setIsHydrated(true);
                return;
            }

            try {
                const currentUserResponse = await userApi.getCurrentUser();
                const currentUserParty = await userCurrentPartyApi.getCurrentUserParty();
                if (!isCancelled) {
                    dispatch(setLoggedIn());
                    dispatch(setCurrentUser(currentUserResponse));
                    dispatch(setCurrentParty(currentUserParty));
                    setIsHydrated(true);

                    const initialPath = getInitialPath();
                    if (REDIRECT_WHITELIST.includes(pathname) && initialPath) {
                        history.replace(initialPath);
                        deleteInitialPath();
                    }
                }
            } catch (e) {
                onAuthError(e as GenericError);
                setIsHydrated(true);
            }
        };

        fetch();

        return () => {
            isCancelled = true;
        };
    }, [
        csrfToken,
        currentUser,
        deleteInitialPath,
        dispatch,
        getInitialPath,
        history,
        onAuthError,
        pathname,
        setInitialPath,
    ]);

    return isHydrated;
};

export default useAuthRehydrate;
